import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { friendsApi, usersApi } from '../api/friendsApi';
import { ConfirmDialog } from './Modals';
import { useAuth } from '../auth/AuthContext';
import type { Friend, FriendRequest, UserMatch } from '../../../shared/types';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Add-a-friend, incoming requests, and current friends. Shared by the Decks
 *  page (Friends tab) and the Profile page. `onChanged` lets the host refresh
 *  whatever depends on the friend list (friends' decks, profile state). */
export default function FriendsPanel({ onChanged }: { onChanged: () => void }) {
  const { getToken, refresh } = useAuth();
  const [friends, setFriends] = useState<Friend[]>([]);
  const [requests, setRequests] = useState<FriendRequest[]>([]);
  const [query, setQuery] = useState('');
  const [msg, setMsg] = useState('');
  const [matches, setMatches] = useState<UserMatch[] | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<{ userId: string; name: string } | null>(null);

  const load = useCallback(() => {
    friendsApi.list(getToken, refresh).then(setFriends).catch(() => {});
    friendsApi.requests(getToken, refresh).then(setRequests).catch(() => {});
  }, [getToken, refresh]);
  useEffect(() => { load(); }, [load]);

  const send = useCallback(async (body: { userId?: string; email?: string }) => {
    setMsg('');
    try {
      await friendsApi.sendRequest(body, getToken, refresh);
      setQuery(''); setMatches(null); setMsg('Request sent.'); load(); onChanged();
    } catch (err) { setMsg(err instanceof Error ? err.message : 'Could not send request'); }
  }, [getToken, refresh, load, onChanged]);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    const value = query.trim();
    if (!value) return;
    setMsg(''); setMatches(null);
    if (value.includes('@')) return send({ email: value });
    if (UUID_RE.test(value)) return send({ userId: value });
    try {
      const results = await usersApi.search(value, getToken, refresh);
      if (results.length === 0) setMsg('No user found with that name.');
      else if (results.length === 1) send({ userId: results[0].userId });
      else { setMatches(results); setMsg('Multiple people have that name — pick one:'); }
    } catch (err) { setMsg(err instanceof Error ? err.message : 'Search failed'); }
  }
  async function accept(id: string) { await friendsApi.accept(id, getToken, refresh); load(); onChanged(); }
  async function decline(id: string) { await friendsApi.decline(id, getToken, refresh); load(); }
  async function doUnfriend(id: string) {
    setConfirmRemove(null);
    await friendsApi.remove(id, getToken, refresh); load(); onChanged();
  }

  return (
    <div className="friends-panel">
      <form className="friend-add" onSubmit={add}>
        <input type="text" placeholder="Add by username, email, or ID" value={query} onChange={e => setQuery(e.target.value)} />
        <button type="submit">Add</button>
      </form>
      {msg && <p className="muted friend-msg">{msg}</p>}
      {matches && (
        <div className="friend-matches">
          {matches.map(m => (
            <div key={m.userId} className="friend-item">
              <span className="friend-name">{m.displayName} <span className="muted friend-id">#{m.userId.slice(0, 8)}</span></span>
              <button type="button" onClick={() => send({ userId: m.userId })}>Add</button>
            </div>
          ))}
        </div>
      )}

      {requests.length > 0 && (
        <div className="friend-section">
          <h3>Requests</h3>
          {requests.map(r => (
            <div key={r.fromId} className="friend-item">
              <Link to={`/user/${r.fromId}`} className="friend-name">{r.fromName}</Link>
              <div className="friend-actions">
                <button type="button" onClick={() => accept(r.fromId)}>Accept</button>
                <button type="button" className="btn-danger" onClick={() => decline(r.fromId)}>Decline</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="friend-section">
        <h3>Friends <span className="muted">· {friends.length}</span></h3>
        {friends.length === 0 && <p className="muted">No friends yet — add one by email above.</p>}
        {friends.map(f => (
          <div key={f.userId} className="friend-item">
            <Link to={`/user/${f.userId}`} className="friend-name">{f.displayName}</Link>
            <button type="button" className="btn-danger" onClick={() => setConfirmRemove({ userId: f.userId, name: f.displayName })}>Remove</button>
          </div>
        ))}
      </div>

      {confirmRemove && (
        <ConfirmDialog title={`Remove ${confirmRemove.name}?`} message="They'll be removed from your friends." confirmLabel="Remove" danger
          onConfirm={() => doUnfriend(confirmRemove.userId)} onCancel={() => setConfirmRemove(null)} />
      )}
    </div>
  );
}
